import React, { useState } from 'react';
import { toast } from '../../../utils/notify';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import ProfileImage from '../ProfileImage';
import VipIcon from '../../Icons/VipIcons';
import { Button, SecondaryButton } from '../Button';
import { followService } from '../../../services/followService';
import { useAuth } from '../../../context/AuthContext';

/**
 * SearchUserCard - Bento-style user card for search results
 * Matches the glassmorphism style from Profile BentoCard
 * Features: Avatar, Name, Username, Stats (VIPS/FANS), Follow/Subscribe buttons
 */

const CardContainer = styled.div`
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  padding: 14px;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
              box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 12px;

  /* Top shine line - glassmorphism effect */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
    opacity: 0.5;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 8px 24px rgba(0, 0, 0, 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 14px;
    gap: 10px;
  }

  /* Dark theme support */
  @media (prefers-color-scheme: dark) {
    background: ${({ theme }) => theme.colors.cardBg || theme.colors.bg2};
    border: 1px solid ${({ theme }) => theme.colors.border};
    box-shadow: 
      0 4px 20px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);

    &::before {
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
    }

    &:hover {
      box-shadow: 
        0 8px 24px rgba(0, 0, 0, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.15);
    }
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

const AvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const OnlineIndicator = styled.span`
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 10px;
  height: 10px;
  background: #22c55e;
  border: 2px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(34, 197, 94, 0.5);

  @media (prefers-color-scheme: dark) {
    border-color: ${({ theme }) => theme.colors.cardBg || theme.colors.bg2};
  }
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const FullName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary || theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const VerifiedBadge = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primaryPink};
  flex-shrink: 0;
`;

const Username = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* Stats Row - VIPS, FANS, POSTS */
const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  
  @media (prefers-color-scheme: dark) {
    border-color: ${({ theme }) => theme.colors.border}40;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex: 1;
`;

const StatValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary || theme.colors.text};
`;

const StatLabel = styled.span`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatLabelHighlight = styled.span`
  color: ${({ theme }) => theme.colors.primaryLight || theme.colors.primaryPink};
`;

const StatLabelNormal = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* Action Buttons */
const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SearchUserCard = ({
  user,
  showStats = false,
  showActions = true,
  showCreatorBadge = true,
  avatarSize = '56px',
  onSubscribe,
  onClick,
  className,
}) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  // Use API fields: isFollowing and isSubscribed from backend
  const [isFollowing, setIsFollowing] = useState(user.isFollowing || false);
  const [isSubscribed] = useState(user.isSubscribed || false);
  const [followLoading, setFollowLoading] = useState(false);
  const [localFollowersCount, setLocalFollowersCount] = useState(user.followersCount || 0);

  const isOwnProfile = currentUser?.id === user.id;

  const handleCardClick = (e) => {
    // Don't navigate if clicking on buttons
    if (e.target.closest('button')) return;
    
    if (onClick) {
      onClick(user);
    } else {
      navigate(`/${user.username}`);
    }
  };

  const handleFollow = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    setFollowLoading(true);
    try {
      if (isFollowing) {
        await followService.unfollowUser(user.id);
        setLocalFollowersCount(prev => Math.max(0, prev - 1));
      } else {
        await followService.followUser(user.id);
        setLocalFollowersCount(prev => prev + 1);
      }
      setIsFollowing(!isFollowing);
    } catch (error) {
      console.error('Follow action failed:', error);
      toast.error("Couldn't update follow. Please try again.");
    } finally {
      setFollowLoading(false);
    }
  };

  const handleSubscribe = (e) => {
    e.stopPropagation();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    if (onSubscribe) {
      onSubscribe(user);
    } else {
      // Navigate to user profile subscription section
      navigate(`/${user.username}`);
    }
  };

  const formatCount = (count) => {
    if (count >= 1000000) {
      return (count / 1000000).toFixed(1) + 'M';
    }
    if (count >= 1000) {
      return (count / 1000).toFixed(1) + 'K';
    }
    return count?.toString() || '0';
  };

  return (
    <CardContainer className={className}>
      {/* Top Row - Avatar and User Info */}
      <TopRow onClick={handleCardClick}>
        <AvatarWrapper>
          <ProfileImage user={user} size={avatarSize} />
          {user.isOnline && <OnlineIndicator />}
        </AvatarWrapper>
        
        <UserInfo>
          <NameRow>
            <FullName>{user.fullName || user.displayName || user.username}</FullName>
            {user.verified && (
              <VerifiedBadge>
                <VipIcon name="VerifiedBadge" size={16} />
              </VerifiedBadge>
            )}
          </NameRow>
          <Username>@{user.username}</Username>
        </UserInfo>
      </TopRow>

      {/* Stats Row - VIPS, FANS, POSTS */}
      {showStats && (
        <StatsRow>
          <StatItem>
            <StatValue>{formatCount(user.subscribersCount || 0)}</StatValue>
            <StatLabel>
              <StatLabelHighlight>VIP</StatLabelHighlight>
              <StatLabelNormal>S</StatLabelNormal>
            </StatLabel>
          </StatItem>
          
          <StatItem>
            <StatValue>{formatCount(localFollowersCount)}</StatValue>
            <StatLabel>
              <StatLabelHighlight>FAN</StatLabelHighlight>
              <StatLabelNormal>S</StatLabelNormal>
            </StatLabel>
          </StatItem>
          
          <StatItem>
            <StatValue>{formatCount(user.postsCount || 0)}</StatValue>
            <StatLabel>
              <StatLabelHighlight>POST</StatLabelHighlight>
              <StatLabelNormal>S</StatLabelNormal>
            </StatLabel>
          </StatItem>
        </StatsRow>
      )}

      {/* Action Buttons */}
      {showActions && !isOwnProfile && (
        <ActionsRow>
          {isFollowing ? (
            <SecondaryButton 
              $size="small" 
              $rounded 
              $compact
              $fullWidth
              onClick={handleFollow}
              disabled={followLoading}
            >
              {followLoading ? '...' : 'Following'}
            </SecondaryButton>
          ) : (
            <Button 
              $size="small" 
              $rounded 
              $compact
              $fullWidth
              onClick={handleFollow}
              disabled={followLoading}
            >
              {followLoading ? '...' : 'Follow'}
            </Button>
          )}
          
          {user.creator && user.verified && (
            isSubscribed ? (
              <SecondaryButton 
                $size="small" 
                $rounded 
                $compact
                $fullWidth
                onClick={handleSubscribe}
              >
                Subscribed
              </SecondaryButton>
            ) : (
              <Button 
                $size="small" 
                $rounded 
                $compact
                $fullWidth
                onClick={handleSubscribe}
              >
                Subscribe
              </Button>
            )
          )}
        </ActionsRow>
      )}
    </CardContainer>
  );
};

export default SearchUserCard;
